/* eslint-disable */
import isEmpty from 'lodash/isEmpty';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import classnames from 'classnames';
import { Loader, Alert } from '@pwc/pwc-deals-ui';
import {
  Constants,
  ErrorUtilities,
  USER_ROLES,
  inlineCommentLicalization,
} from '@pwc/pwc-deals-web-common';

import {
  getIsThirdPartyMember,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import featureFlags, {
  isInlineCommentingEnabled,
} from 'common/helpers/featureFlags';
import useMobile from 'common/hooks/useMobile';
import AutoLock from 'common/components/AutoLock';
import { SyncedInfoProvider } from 'common/components/Autosync/SyncedInfoProvider';
import { getWorkflowLoading } from 'common/redux/workflow/workflowSlice';
import updateEntityVersion from 'common/actions/updateEntityVersion';
import { FormLayout } from 'common/components/layouts';
import ModalConfirmation from 'common/components/ModalConfirmation';
import { getViewClientInfo } from 'common/helpers/viewClientVersion';
import { useApprovalWorkflow } from 'common/hooks/useApprovalWorkflow';
import { useIsNonCommentableContent } from 'common/hooks/useIsNonCommentableContent';
import useVersionToggle from 'common/hooks/useVersionToggle';
import useIsMountedRef from 'common/hooks/useIsMountedRef';
import { LoadingError as LoadingErrorVersioning } from 'features/versioning/common/LoadingError';
import { getFreeformAnalysisSelector } from 'features/analyses/editFreeformAnalysisSlice';
import { useTranslation } from 'react-i18next';

import {
  showModalError,
  showMoreMenuError,
  showBannerError,
  hideGlobalError,
} from '../../../../common/redux/errors/errorsSlice';
import {
  analysesGroupsListPath,
  editAnalysisPath,
} from '../../../../common/services/urlService';
import userTokenService from '../../../../common/services/security/userTokenService';
import NewConnectedContent from '../../../narratives/ConnectedContent/NewConnectedContent';
import useConnectedContent from '../../../narratives/hooks/useConnectedContent';
import {
  deleteAnalysis,
  updateAnalysisWorkflowStatus,
} from '../../analysesService';
import { getAnalysisWorkflowStatus } from '../../../workflow/workflowService';
import {
  getAnalysisStatus,
  resetAnalysis,
  selectAnalysisContent,
  selectAnalysisVersions,
  selectDraftVersion,
  selectSharedVersion,
  fetchAnalysisContentStart,
  fetchAnalysisContentEnd,
  updateSelectionVersion,
  selectedVersionStatus,
  updateVersions,
} from '../../analysisSlice';

import {
  loadAnalysisVersions,
  loadAnalysisWithDraftContent,
  loadAnalysisWithSharedContent,
  loadAnalysisWithSharedContentPrevious,
} from '../../analysisSliceThunk';
import '../../common/analysis-detail-screen.scss';
import AnalysisControlsHeader from '../../common/AnalysisControlsHeader';
import TagList from '../../common/TagList/TagList';
import AnalysisDetailScreen from './AnalysisDetailScreen';
import VersionToggleContainer from '../../../../common/components/VersionToggle/VersionToggleContainer';
import './AnalysisDetailScreenContainer.scss';
import { entitySharedStatusUpdateSuccess } from '../../../comments/commentsSlice';
import BannerError from '../../../../common/components/BannerError';
import { getWorkspaceId } from '../../../project/projectSlice';
import LoadingError from '../../../../common/components/LoadingError';
import { useShowInlineComments } from '../../../../common/hooks/useShowInlineComments';

const AnalysisDetailScreenContainer = () => {
  const { setDefaultCloseCommentPanel } = useShowInlineComments();
  const isMobile = useMobile();
  const isMountedRef = useIsMountedRef();
  const { projectId, analysisId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);
  const { t } = useTranslation();

  useEffect(() => {
    if (isPwcUser) {
      dispatch(loadAnalysisVersions(projectId, analysisId));
    }
  }, [dispatch, isPwcUser, projectId, analysisId]);

  const analysisContent = useSelector(selectAnalysisContent);
  const analysisStatus = useSelector(getAnalysisStatus);
  const onLoadingPreviousVersionErrorRetryFN = useRef(undefined);
  const onLoadingPreviousVersionError = useCallback(() => {
    if (analysisStatus.hasError) {
      return onLoadingPreviousVersionErrorRetryFN?.current();
    }

    return undefined;
  }, [analysisStatus.hasError]);
  const workflowLoading = useSelector(getWorkflowLoading);
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] =
    useState(false);
  const selectedVersions = useSelector(selectAnalysisVersions);
  const draftVersion = useSelector(selectDraftVersion);
  const sharedVersion = useSelector(selectSharedVersion);
  const selectedVersion = useSelector(selectedVersionStatus);
  const { analysis } = useSelector(getFreeformAnalysisSelector);
  const previousSharedContentId = useRef(sharedVersion?.contentId);

  const isNonCommentableContent = useIsNonCommentableContent();
  const isReadOnly = userTokenService.isPwcReadOnlyRole(projectId);

  const showNonCommentableContent =
    isInlineCommentingEnabled && isNonCommentableContent && !isReadOnly;

  const { isEntitySharedWithClient, commentCounts } = useVersionToggle(
    isMountedRef.current,
    draftVersion,
    sharedVersion
  );
  const linkedConnections = useConnectedContent(
    analysisContent && analysisContent.contentId
  );
  const workspaceId = useSelector(getWorkspaceId);
  const contextStatusInfoItem = useMemo(
    () => ({
      isViewMode: true,
      isEditMode: false,
      versionStatus: analysisContent?.versionStatus,
      userViewStatus: analysisContent?.userViewStatus,
      shareWithAll: analysisContent?.shareWithAll,
    }),
    [
      analysisContent?.shareWithAll,
      analysisContent?.userViewStatus,
      analysisContent?.versionStatus,
    ]
  );

  const isSelectedVersionADraft = useMemo(() => {
    if (selectedVersion && selectedVersions) {
      return selectedVersions.some(
        (x) =>
          x.contentId === selectedVersion.contentId &&
          x.versionStatus === Constants.VERSION_STATUS.DRAFT.value
      );
    }

    return false;
  }, [selectedVersion, selectedVersions]);

  const hasSharedSelected =
    selectedVersions?.some(({ isSelected }) => isSelected) || false;

  const hasSharedSelectedPrevious =
    selectedVersions?.some(
      ({ isSelected, isPreviousVersion }) => isSelected && isPreviousVersion
    ) || false;

  const hasLatestButNotDraftVersion =
    selectedVersions?.some(
      ({ isSelected, isPreviousVersion }) => isSelected && !isPreviousVersion
    ) || false;

  /**
   * Only show for draft, hide the rest
   */
  const hideTextModifiedBy = useMemo(() => {
    if (draftVersion) {
      if (!isSelectedVersionADraft) {
        return true;
      }

      return false;
    }

    return true;
  }, [draftVersion, isSelectedVersionADraft]);

  const dispatchUpdateSelectionVersion = useCallback(
    (selectedVersionInput) => {
      if (
        selectedVersion?.contentId !== selectedVersionInput.contentId ||
        !selectedVersion
      ) {
        dispatch(updateSelectionVersion(selectedVersionInput));
      }
    },
    [dispatch, selectedVersion]
  );

  const handleEdit = () => {
    dispatch(hideGlobalError());
    history.push(editAnalysisPath(projectId, analysisId));
  };

  const getDraftContent = useCallback(() => {
    const { contentId } = draftVersion;
    dispatch(entitySharedStatusUpdateSuccess(false));
    dispatch(
      loadAnalysisWithDraftContent(
        projectId,
        contentId,
        workspaceId,
        contextStatusInfoItem
      )
    );
    dispatchUpdateSelectionVersion({ contentId });
  }, [
    draftVersion,
    dispatch,
    projectId,
    workspaceId,
    contextStatusInfoItem,
    dispatchUpdateSelectionVersion,
  ]);

  const getSharedContent = useCallback(() => {
    if (
      isEntitySharedWithClient &&
      sharedVersion?.contentId &&
      sharedVersion.contentId === previousSharedContentId.current
    ) {
      return;
    }
    previousSharedContentId.current = sharedVersion?.contentId;

    const { contentId } = sharedVersion;

    dispatch(entitySharedStatusUpdateSuccess(true));
    dispatch(
      loadAnalysisWithSharedContent({
        analysisID: contentId,
        contextStatusInfoItem,
        isClientUser: !isPwcUser,
        projectID: projectId,
      })
    );
    dispatchUpdateSelectionVersion({ contentId });
  }, [
    isEntitySharedWithClient,
    sharedVersion,
    dispatch,
    contextStatusInfoItem,
    isPwcUser,
    projectId,
    dispatchUpdateSelectionVersion,
  ]);

  const getSharedContentPreviousOrCurrentSelected = useCallback(
    ({ contentId }) => {
      dispatch(entitySharedStatusUpdateSuccess(true));
      dispatch(
        loadAnalysisWithSharedContentPrevious({
          analysisID: contentId,
          contextStatusInfoItem,
          isClientUser: !isPwcUser,
          projectID: projectId,
        })
      );
      dispatchUpdateSelectionVersion({ contentId });
    },
    [
      dispatch,
      projectId,
      isPwcUser,
      contextStatusInfoItem,
      dispatchUpdateSelectionVersion,
    ]
  );

  const renderBody = analysisContent && analysisContent.contentThreadId;

  useEffect(() => {
    if (isMountedRef.current) {
      if (featureFlags.isVersioningPhase1Enabled) {
        // handle redirect from edit screen, edit screen should already populate versions, so do not touch
        if (
          !selectedVersions ||
          analysis?.contentThreadId !== analysisContent?.contentThreadId
        ) {
          dispatch(loadAnalysisVersions(projectId, analysisId));
        }
      } else {
        dispatch(loadAnalysisVersions(projectId, analysisId));
      }
    }

    return () => dispatch(resetAnalysis());
    // do not add selectedVersions, it's going to freeze loop, we only want to check it once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, projectId, analysisId, isMountedRef, isPwcUser]);

  useEffect(() => {
    if (isMountedRef.current && selectedVersions) {
      if (featureFlags.isVersioningPhase1Enabled) {
        const versionPreviousSelected = selectedVersions.find(
          ({ isPreviousVersion, isSelected }) => isPreviousVersion && isSelected
        );

        if (versionPreviousSelected) {
          onLoadingPreviousVersionErrorRetryFN.current = () =>
            getSharedContentPreviousOrCurrentSelected({
              contentId: versionPreviousSelected.contentId,
            });
          onLoadingPreviousVersionErrorRetryFN.current();
          dispatch(updateEntityVersion(versionPreviousSelected.versionNumber));
        } else if (
          draftVersion &&
          !isEmpty(draftVersion) &&
          !sharedVersion?.isSelected &&
          !isThirdPartyMember
        ) {
          if (!analysisStatus.isLoading && !analysisStatus.isDetailSuccess) {
            getDraftContent();
          }
          dispatch(updateEntityVersion(draftVersion.versionNumber));
        } else if (sharedVersion && !isEmpty(sharedVersion)) {
          // TODO: Updated from  !analysisStatus.isLoading && !analysisStatus.isSharedDetailSuccess to below.
          // When user shares with client, the resetAnalysis is not dispatched. So the condition was never true because it'll be always isLoading or isSharedDetailSuccess true
          // && works only when the page is loaded in the client view mode
          if (
            !analysisStatus.isLoading ||
            !analysisStatus.isSharedDetailSuccess
          ) {
            getSharedContent();
          }
          dispatch(updateEntityVersion(sharedVersion.versionNumber));
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (draftVersion && !isEmpty(draftVersion)) {
          getDraftContent();
          dispatch(updateEntityVersion(draftVersion.versionNumber));
        } else if (sharedVersion && !isEmpty(sharedVersion)) {
          getSharedContent();
          dispatch(updateEntityVersion(sharedVersion.versionNumber));
        }
      }
    }
    /**
     * Don't add the callbacks which depends on selectedVersion which is updated all the time
     * getDraftContent, getDraftContent, getSharedContentPreviousOrCurrentSelected
     */

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    analysisId,
    draftVersion,
    projectId,
    selectedVersions,
    sharedVersion,
    dispatch,
    workspaceId,
    isMountedRef,
    isPwcUser,
    analysisContent,
  ]);

  const handleDelete = async () => {
    try {
      await deleteAnalysis(projectId, analysisContent.contentThreadId);
      history.push(analysesGroupsListPath(projectId));
    } catch (e) {
      setIsDeleteConfirmationVisible(false);
      dispatch(
        showModalError(Constants.MoreMenuErrorType.UNABLE_TO_SAVE_CHANGE)
      );
    }
  };

  const workflowActions = useApprovalWorkflow(
    getAnalysisWorkflowStatus,
    async (_projectId, contentId, step) => {
      try {
        dispatch(fetchAnalysisContentStart());
        const response = await updateAnalysisWorkflowStatus(
          _projectId,
          contentId,
          step
        );
        if (!response) {
          throw new Error('Failed to share content');
        }
      } catch (e) {
        if (ErrorUtilities.hasLibraryItemCorruptedError(e)) {
          dispatch(
            showMoreMenuError({
              itemId: contentId,
              moreMenuErrorType:
                Constants.MoreMenuErrorType.LIBRARY_ITEM_CORRUPTED,
            })
          );
        } else {
          dispatch(showBannerError('Failed to share content'));
        }
        dispatch(fetchAnalysisContentEnd());
        return;
      }
      dispatch(fetchAnalysisContentEnd());
      dispatch(loadAnalysisVersions(projectId, analysisId));
    },
    projectId,
    draftVersion && draftVersion.contentId,
    analysisContent,
    selectedVersions
  );

  const showClientVersion = useCallback(async () => {
    if (!analysisStatus.isLoading && sharedVersion) {
      if (featureFlags.isVersioningPhase1Enabled) {
        dispatch(
          updateVersions(
            /**
             * this component starts with or without versioning MFE, the versions in the store could be completely outdated or not there
             * so what happens here is we modify the versions so useEffect matches the logic to load previous version
             */
            (selectedVersions || []).reduce((acc, item) => {
              const x = { ...item };
              if (
                x.versionStatus === Constants.VERSION_STATUS.SHARED.value ||
                x.versionStatus ===
                  Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
              ) {
                Object.assign(x, {
                  isPreviousVersion: false,
                  isSelected: true,
                });
              }
              acc.push(x);
              return acc;
            }, [])
          )
        );
        dispatchUpdateSelectionVersion({
          contentId:
            selectedVersions?.find(
              ({ versionStatus }) =>
                versionStatus === Constants.VERSION_STATUS.SHARED.value ||
                versionStatus ===
                  Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
            ).contentId || sharedVersion.contentId,
        });
      } else {
        getSharedContent();
        dispatch(updateEntityVersion(sharedVersion.versionNumber));
      }
    }
    setDefaultCloseCommentPanel();
  }, [
    dispatchUpdateSelectionVersion,
    dispatch,
    getSharedContent,
    analysisStatus.isLoading,
    selectedVersions,
    sharedVersion,
    setDefaultCloseCommentPanel,
  ]);
  const showDraftVersion = useCallback(async () => {
    if (!analysisStatus.isLoading && draftVersion) {
      if (featureFlags.isVersioningPhase1Enabled) {
        dispatch(
          updateVersions(
            // needed because useEffect will only match draft when shared is not selected
            (selectedVersions || []).reduce((acc, item) => {
              const x = { ...item };
              if (
                x.versionStatus === Constants.VERSION_STATUS.SHARED.value ||
                x.versionStatus ===
                  Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
              ) {
                x.isSelected = false;
              }
              acc.push(x);
              return acc;
            }, [])
          )
        );
        dispatchUpdateSelectionVersion({ contentId: draftVersion.contentId });
      } else {
        getDraftContent();
        dispatch(updateEntityVersion(draftVersion.versionNumber));
      }
      setDefaultCloseCommentPanel();
    }
  }, [
    dispatch,
    dispatchUpdateSelectionVersion,
    draftVersion,
    getDraftContent,
    analysisStatus.isLoading,
    selectedVersions,
    setDefaultCloseCommentPanel,
  ]);

  const viewClient = getViewClientInfo(
    showClientVersion,
    showDraftVersion,
    selectedVersions,
    analysisContent && analysisContent.versionNumber,
    analysisContent && analysisContent.versionStatus
  );

  const { versionStatus = 0 } = analysisContent || {};
  const isClientView =
    !isPwcUser ||
    viewClient.isOnClientVersion ||
    versionStatus === Constants.VERSION_STATUS.SHARED.value;

  const commentingAutoLockContentId = isClientView
    ? [`content-${analysisContent?.contentId}`]
    : [analysisContent?.contentThreadId];

  const currentCommetingAutoLockContentId = commentingAutoLockContentId[0];

  const renderError = useCallback(() => {
    if (!analysisStatus.hasError) {
      return null;
    }

    switch (analysisStatus.errorType) {
      case 'previousVersion':
        return (
          <>
            {isMobile && (
              <AnalysisControlsHeader
                analysis={{}}
                disableMoreMenu={
                  hasLatestButNotDraftVersion ? false : hasSharedSelected
                }
                hideEdit
                disableEdit
                isInternalUser={isPwcUser}
                onDelete={() => setIsDeleteConfirmationVisible(true)}
                rightAlignItemsToright
                viewClient={viewClient}
                workflowActions={workflowActions}
                commentingAutoLockContentId={currentCommetingAutoLockContentId}
              />
            )}

            <LoadingErrorVersioning
              contentMainType="unableToLoadPreviousVersion"
              propsActionButton={{
                children: 'Retry',
                onClick: onLoadingPreviousVersionError,
              }}
              propsTitle1={{
                children: 'Unable to load this version',
              }}
            />
          </>
        );

      default:
        return <LoadingError />;
    }
  }, [
    analysisStatus.hasError,
    analysisStatus.errorType,
    isMobile,
    hasLatestButNotDraftVersion,
    hasSharedSelected,
    isPwcUser,
    viewClient,
    workflowActions,
    currentCommetingAutoLockContentId,
    onLoadingPreviousVersionError,
  ]);

  if ((analysisStatus.isLoading && sharedVersion) || workflowLoading) {
    return <Loader dataTestId="structured-loading-indicator" />;
  }

  const headerComponent = analysisContent && (
    <>
      <ModalConfirmation
        show={isDeleteConfirmationVisible}
        onCancel={() => setIsDeleteConfirmationVisible(false)}
        onConfirm={handleDelete}
        confirmTitle={t('areyouSureDeleteMsg')}
        confirmationMessage={t('thisAnalysisWillBeDeleted')}
        confirmButtonText={t('yesDelete')}
      />
      <AnalysisControlsHeader
        commentingAutoLockContentId={currentCommetingAutoLockContentId}
        workflowActions={workflowActions}
        onEdit={handleEdit}
        onDelete={() => setIsDeleteConfirmationVisible(true)}
        analysis={analysisContent}
        viewClient={viewClient}
        isInternalUser={isPwcUser}
        disableEdit={hasSharedSelected || isMobile}
        disableMoreMenu={
          hasLatestButNotDraftVersion ? false : hasSharedSelected
        }
      >
        <VersionToggleContainer
          redirectUrl={`/projects/${projectId}/analyses`}
          config={{
            commentCounts,
            draftVersion,
            sharedVersion,
            draftOnClick: showDraftVersion,
            sharedOnClick: showClientVersion,
          }}
          disableVersionToggleButtons={
            hasLatestButNotDraftVersion ? false : hasSharedSelected
          }
          hideTextVersion={featureFlags.isVersioningPhase1Enabled}
          isEntitySharedWithClient={isEntitySharedWithClient}
          lockedThreadId={analysisContent.contentThreadId}
          mode="view"
          projectId={projectId}
          showSelected={!hasSharedSelectedPrevious}
        />
      </AnalysisControlsHeader>
    </>
  );

  if ((analysisStatus.isLoading && sharedVersion) || workflowLoading) {
    return <Loader dataTestId="structured-loading-indicator" />;
  }

  return (
    <>
      <SyncedInfoProvider value={contextStatusInfoItem}>
        <AutoLock
          projectId={projectId}
          contentThreadIds={commentingAutoLockContentId}
        >
          {renderBody && (
            <div
              className={classnames(
                'analysis-details-screen view-ffa-scroll-component',
                isPwcUser ? 'current-user-is-pwc' : 'current-user-is-client'
              )}
            >
              <div
                className="analysis-main-content2"
                data-testid="analysis-detail-page"
              >
                <div className="global-error-container" />
                <FormLayout headerComponent={headerComponent}>
                  <BannerError />
                  {showNonCommentableContent && (
                    <Alert
                      className="analyses-non-commentable-content-banner"
                      type="info"
                      content={inlineCommentLicalization.nonCommentable(
                        isPwcUser
                      )}
                    />
                  )}
                  <AnalysisDetailScreen
                    disableLinkedContentNavigationLinks={
                      featureFlags.isVersioningPhase1Enabled
                        ? hasSharedSelectedPrevious
                        : false
                    }
                    projectId={projectId}
                    analysis={analysisContent}
                    versions={selectedVersions}
                    linkedConnections={linkedConnections}
                    onEdit={handleEdit}
                    viewClient={viewClient}
                    shareWithAll={isEntitySharedWithClient}
                    commentingAutoLockContentId={
                      currentCommetingAutoLockContentId
                    }
                    hideTextModifiedBy={hideTextModifiedBy}
                  />

                  {analysisContent.tags && analysisContent.tags.length > 0 && (
                    <TagList
                      tags={analysisContent.tags}
                      testId="analysis-tag-list"
                      isCommentingAllowed
                    />
                  )}
                  <NewConnectedContent
                    projectId={projectId}
                    contentId={analysisContent.contentId}
                    viewAsClient={isClientView}
                    linkedConnections={linkedConnections}
                  />
                </FormLayout>
              </div>
            </div>
          )}

          {renderError()}
        </AutoLock>
      </SyncedInfoProvider>
    </>
  );
};

export default AnalysisDetailScreenContainer;
